import { useState, useEffect, useCallback, useRef } from 'react';
import './FitIntelligence.css';

interface FitIntelligenceProps {
  apiBaseUrl: string;
}

const STATS: { label: string; value: string }[] = [
  { label: 'Height', value: '5 ft 7 in' },
  { label: 'Chest', value: '36 in' },
  { label: 'Waist', value: '28 in' },
];

const OWNED_ITEMS: string[] = [
  "Levi's 501, 30/32",
  'Uniqlo Tee, M',
  'J.Crew Blazer, 8',
];

const CHART_ROWS: { size: string; chest: string; waist: string; length: string }[] = [
  { size: 'S', chest: '34', waist: '26', length: '27' },
  { size: 'M', chest: '36', waist: '28', length: '28' },
  { size: 'L', chest: '38', waist: '30', length: '29' },
];

const LOCKED_ROW_INDEX = 1; // M

interface AnimState {
  showHeader: boolean;
  statsRevealed: number; // how many stat rows are shown
  statTypedValues: string[]; // typed value text for each stat row
  statTypingIdx: number; // index of stat currently typing (-1 when none)
  showItemsLabel: boolean;
  itemsCount: number;
  showChart: boolean;
  scanRow: number; // currently scanned row index, -1 when none
  lockedRow: number; // locked row index, -1 when none
  showRecommendation: boolean;
  pulse: boolean;
  fadeOut: boolean;
}

const INITIAL: AnimState = {
  showHeader: false,
  statsRevealed: 0,
  statTypedValues: ['', '', ''],
  statTypingIdx: -1,
  showItemsLabel: false,
  itemsCount: 0,
  showChart: false,
  scanRow: -1,
  lockedRow: -1,
  showRecommendation: false,
  pulse: false,
  fadeOut: false,
};

export function FitIntelligence(_props: FitIntelligenceProps) {
  const [s, setS] = useState<AnimState>(INITIAL);
  const loopRef = useRef(true);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const sleep = useCallback(
    (ms: number) =>
      new Promise<void>(resolve => {
        const id = setTimeout(resolve, ms);
        timeoutsRef.current.push(id);
      }),
    [],
  );

  const runLoop = useCallback(async () => {
    while (loopRef.current) {
      setS({ ...INITIAL, statTypedValues: ['', '', ''] });
      await sleep(600);

      // 1) Header
      if (!loopRef.current) return;
      setS(prev => ({ ...prev, showHeader: true }));
      await sleep(550);

      // 2) Body stats — reveal each row then typewriter the value
      for (let i = 0; i < STATS.length; i++) {
        if (!loopRef.current) return;
        setS(prev => ({ ...prev, statsRevealed: i + 1, statTypingIdx: i }));
        await sleep(120);

        const full = STATS[i].value;
        for (let c = 1; c <= full.length; c++) {
          if (!loopRef.current) return;
          const chars = full.slice(0, c);
          setS(prev => {
            const next = [...prev.statTypedValues];
            next[i] = chars;
            return { ...prev, statTypedValues: next };
          });
          await sleep(30);
        }
        await sleep(220);
      }
      if (!loopRef.current) return;
      setS(prev => ({ ...prev, statTypingIdx: -1 }));
      await sleep(350);

      // 3) Items-you-own label + pills
      if (!loopRef.current) return;
      setS(prev => ({ ...prev, showItemsLabel: true }));
      await sleep(400);

      for (let i = 1; i <= OWNED_ITEMS.length; i++) {
        if (!loopRef.current) return;
        setS(prev => ({ ...prev, itemsCount: i }));
        await sleep(300);
      }
      await sleep(500);

      // 4) Mini size chart appears
      if (!loopRef.current) return;
      setS(prev => ({ ...prev, showChart: true }));
      await sleep(650);

      // Scan rows top to bottom
      for (let i = 0; i < CHART_ROWS.length; i++) {
        if (!loopRef.current) return;
        setS(prev => ({ ...prev, scanRow: i }));
        await sleep(450);
      }

      // Lock onto M row
      if (!loopRef.current) return;
      setS(prev => ({ ...prev, scanRow: -1, lockedRow: LOCKED_ROW_INDEX }));
      await sleep(700);

      // 5) Recommendation pill with pulse
      if (!loopRef.current) return;
      setS(prev => ({ ...prev, showRecommendation: true }));
      await sleep(150);
      if (!loopRef.current) return;
      setS(prev => ({ ...prev, pulse: true }));
      await sleep(900);
      if (!loopRef.current) return;
      setS(prev => ({ ...prev, pulse: false }));

      // 6) Hold final state
      await sleep(2000);

      // 7) Fade out
      if (!loopRef.current) return;
      setS(prev => ({ ...prev, fadeOut: true }));
      await sleep(650);

      await sleep(500);
    }
  }, [sleep]);

  useEffect(() => {
    loopRef.current = true;
    runLoop();
    return () => {
      loopRef.current = false;
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [runLoop]);

  return (
    <div className="fit-intelligence">
      <div className={`fi-card sc-glass${s.fadeOut ? ' fi-fade-out' : ''}`}>

        {/* ── Header ── */}
        <div className={`fi-section${s.showHeader ? ' fi-visible' : ''}`}>
          <div className="sc-section-label">Fit profile</div>
        </div>

        {/* ── Body stats ── */}
        <div className={`fi-section${s.statsRevealed > 0 ? ' fi-visible' : ''}`}>
          <div className="fi-stats-list">
            {STATS.map((stat, i) => (
              <div
                key={stat.label}
                className={`fi-stat-row${i < s.statsRevealed ? ' fi-stat-in' : ''}`}
              >
                <span className="fi-stat-label">{stat.label}</span>
                <span className="fi-stat-value">
                  {s.statTypedValues[i]}
                  {s.statTypingIdx === i && <span className="fi-cursor" />}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Items you own ── */}
        <div className={`fi-section${s.showItemsLabel ? ' fi-visible' : ''}`}>
          <div className="sc-section-label">Items you own</div>
          <div className="fi-pills">
            {OWNED_ITEMS.map((item, i) => (
              <span
                key={item}
                className={`sc-pill fi-pill${i < s.itemsCount ? ' fi-pill-in' : ''}`}
              >
                {item}
              </span>
            ))}
          </div>
        </div>

        {/* ── Size chart ── */}
        <div className={`fi-section${s.showChart ? ' fi-visible' : ''}`}>
          <div className="sc-section-label">Size chart</div>
          <div className="fi-chart sc-glass-subtle">
            <div className="fi-chart-head">
              <span className="fi-chart-cell fi-chart-size">Size</span>
              <span className="fi-chart-cell">Chest</span>
              <span className="fi-chart-cell">Waist</span>
              <span className="fi-chart-cell">Length</span>
            </div>
            {CHART_ROWS.map((row, i) => {
              const isScan = s.scanRow === i;
              const isLocked = s.lockedRow === i;
              return (
                <div
                  key={row.size}
                  className={`fi-chart-row${isScan ? ' fi-chart-row-scan' : ''}${isLocked ? ' fi-chart-row-locked' : ''}`}
                >
                  <span className="fi-chart-cell fi-chart-size">{row.size}</span>
                  <span className="fi-chart-cell">{row.chest}</span>
                  <span className="fi-chart-cell">{row.waist}</span>
                  <span className="fi-chart-cell">{row.length}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Recommendation ── */}
        <div className={`fi-section fi-recommend-section${s.showRecommendation ? ' fi-visible' : ''}`}>
          <div className={`fi-recommend${s.pulse ? ' fi-pulse-once' : ''}`}>
            <span className="sc-check" aria-hidden="true">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.5 5.25L4 7.5L8.5 2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <div className="fi-recommend-text">
              <div className="fi-recommend-title">Recommended: M</div>
              <div className="fi-recommend-sub">High confidence</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
